<template>
  <div class="date-picker" :isInvalid="isInvalid">
    <b-form-input
      type="text"
      maxlength="10"
      ref="input"
      :state="error || isInvalid ? false : null"
      :value="value"
      :placeholder="placeholder"
      :formatter="maskDate"
      @keyup="update"
      @change="setTypedDate"
      />
    <div class="date-picker__icon d-flex justify-content-center pointer">
      <img
        src="@/assets/images/icon-calendar.svg"
        @click="toggleCalendar"
        >
    </div>
    <div
      class="date-picker__calendar"
      :class="[anchorClass, { 'date-picker__calendar--show': show}]"
      ref="calendar"
      >
      <vuejs-datepicker
        :language="language"
        :placeholder="placeholder"
        :inline="true"
        :full-month-name="true"
        :value="valueAsDate"
        :disabled-dates="disabledDates"
        @input="setDate"
        @selected="closeCalendar"
        ref="vjsCalendar"
        />
    </div>
    <b-form-invalid-feedback v-if="error">{{error}}</b-form-invalid-feedback>
  </div>
</template>

<script>
import VuejsDatepicker from 'vuejs-datepicker';
import { ptBR } from 'vuejs-datepicker/dist/locale';
import { getDateFromString, getStringFromDate } from '@/utils/dateUtils';
import DOMUtils from '@/utils/DOMUtils';

const ANCHORS = ['left', 'right'];
const dateRegEx = /([0-9]{2})\/([0-9]{2})\/([0-9]{4})/;

function isValidDate(value) {
  if (value && !dateRegEx.test(value)) {
    console.error(`Invalid date. Expected '${value}' to have 'dd/MM/yyyy' format`);
    return false;
  }
  return true;
}

export default {
  name: 'DatePicker',
  props: {
    value: {
      type: String,
      validator: isValidDate,
    },
    minDate: {
      type: String,
      validator: isValidDate,
    },
    maxDate: {
      type: String,
      validator: isValidDate,
    },
    placeholder: String,
    anchor: {
      type: String,
      default: ANCHORS[0],
      validator(value) {
        if (!ANCHORS.includes(value)) {
          console.error(`Invalid anchor. Expected '${value}' to be 'left' or 'right'`);
          return false;
        }
        return true;
      },
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VuejsDatepicker,
  },
  data() {
    return {
      language: ptBR,
      anchorClass: null,
      error: null,
      show: false,
    };
  },
  computed: {
    valueAsDate() {
      return dateRegEx.test(this.$props.value) ? getDateFromString(this.$props.value) : null;
    },
    disabledDates() {
      let to;
      let from;
      if (this.$props.minDate) to = getDateFromString(this.$props.minDate);
      if (this.$props.maxDate) from = getDateFromString(this.$props.maxDate);
      if (to && from && to > from) throw new Error('Invalid date range, max-date must be after min-date');
      return { to, from };
    },
  },
  methods: {
    setDate(date) {
      let modelValue = '';
      if (date) {
        this.validateDateRange(date);
        modelValue = getStringFromDate(date);
      }

      this.$emit('input', modelValue);
      this.$emit('change', modelValue);
    },
    toggleCalendar() {
      if (this.$data.show) {
        this.closeCalendar();
      } else {
        this.$data.show = true;
        this.scrollToCalendar();
        document.addEventListener('click', this.handleClose);
      }
    },
    scrollToCalendar() {
      setTimeout(() => {
        const rect = this.$refs.calendar.getBoundingClientRect();
        const delta = rect.top + rect.height - window.outerHeight;

        if (delta > 0) {
          this.$refs.calendar.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    },
    closeCalendar() {
      this.$data.show = false;
      document.removeEventListener('click', this.handleClose);
    },
    handleClose(event) {
      if (!DOMUtils.isChildOf(event.target, this.$el)) this.closeCalendar();
    },
    setTypedDate(value) {
      const date = dateRegEx.test(value) ? getDateFromString(value) : null;
      this.setDate(date);
    },
    validateDateRange(date) {
      if (this.$props.minDate && !this.validateMinDate(date)) {
        this.$data.error = `A data deve ser superior à ${this.$props.minDate}`;
      } else if (this.$props.maxDate && !this.validateMaxDate(date)) {
        this.$data.error = `A data deve ser inferior à ${this.$props.maxDate}`;
      } else {
        this.$data.error = null;
      }
    },
    validateMinDate(date) {
      const minDate = getDateFromString(this.$props.minDate);
      return date >= minDate;
    },
    validateMaxDate(date) {
      const maxDate = getDateFromString(this.$props.maxDate);
      return date <= maxDate;
    },
    maskDate(dateString) {
      const dateChars = dateString.replace(/[^\d]/gm, '').split('');

      let formatted = '';
      formatted += dateChars.splice(0, 2).join('');
      formatted += dateChars.length ? `/${dateChars.splice(0, 2).join('')}` : '';
      formatted += dateChars.length ? `/${dateChars.splice(0, 4).join('')}` : '';

      return formatted;
    },
    update() {
      this.$forceUpdate();
    },
  },
  created() {
    this.$data.anchorClass = `date-picker__calendar--anchor-${this.$props.anchor}`;
    if (this.valueAsDate) this.validateDateRange(this.valueAsDate);
  },
};
</script>

<style lang="scss" scoped>
.date-picker {
  position: relative;
}

.date-picker__calendar {
  position: absolute;
  display: none;
  z-index: 2;
}

.date-picker__calendar--anchor-left {
  left: 0;
}

.date-picker__calendar--anchor-right {
  right: 0;
}

.date-picker__calendar--show {
  display: block;
}

.date-picker__icon {
  $size: 25px;
  width: $size;
  height: $size;
  position: absolute;
  right: 4px;
  top: 4px;
}

.form-control.is-invalid {
  background-image: none;
}

/deep/ {
  .vdp-datepicker__calendar {
    border-color: $color-1;
    width: 270px;
    box-shadow: 0 0 5px 0 rgba(0,0,0,0.75);
  }
  .cell.day.weekend {
    background-color: lighten($color-1, 40%);
  }
  .cell.day.selected {
    background-color: $color-1;
    color: $text-light;
  }
  .vdp-datepicker__calendar .next,
  .vdp-datepicker__calendar .prev {
    color: $color-1;
  }
  .vdp-datepicker__calendar .next::after {
    border-left-color: $color-1;
  }
  .vdp-datepicker__calendar .prev::after {
    border-right-color: $color-1;
  }
}
</style>
